/**
 * Format currency for payment UI display
 *
 * Task: T-P4-D2-F5-3
 *
 * Always shows 2 decimal places for "money math" consistency.
 * This is different from formatPrice which omits decimals for whole numbers.
 *
 * Examples:
 * - 30 → "$30.00"
 * - 30.5 → "$30.50"
 * - 120.99 → "$120.99"
 * - null or 0 → "$0.00"
 */
export function formatCurrency(amount: number | null, currency = 'USD'): string {
  const safeAmount = amount ?? 0;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeAmount);
}

/**
 * Calculate deposit amount from session price
 *
 * Handles both PERCENTAGE and FIXED deposit types.
 * Uses Math.round to avoid floating point precision issues.
 *
 * @param sessionPrice - Full session price in dollars
 * @param depositAmount - Deposit amount (percentage 0-100 or fixed dollar amount)
 * @param depositType - 'PERCENTAGE' or 'FIXED'
 * @returns Calculated deposit amount in dollars, rounded to cents
 */
export function calculateDepositAmount(
  sessionPrice: number | null,
  depositAmount: number | null,
  depositType: 'PERCENTAGE' | 'FIXED' | null,
): number {
  const price = sessionPrice ?? 0;
  const deposit = depositAmount ?? 0;

  if (depositType === 'PERCENTAGE') {
    // Round to cents: multiply by 100, round, divide by 100
    return Math.round(price * (deposit / 100) * 100) / 100;
  }

  if (depositType === 'FIXED') {
    return deposit;
  }

  // No deposit type specified - return full price
  return price;
}
